import React, { useState, useEffect } from 'react';
import { Box, IconButton, Slider, Typography, useMediaQuery, useTheme, Avatar, CircularProgress, keyframes } from '@mui/material';
import { PlayArrow, Pause, SkipNext, SkipPrevious, VolumeUp, VolumeOff, MusicNote } from '@mui/icons-material';
import { useAudio } from '../context/AudioContext';
import { useNavigate } from 'react-router-dom';

// Define the pulsing animation
const pulse = keyframes`
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.6;
  }
`;

const Player = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [isLoading, setIsLoading] = useState(false);
  
  const {
    currentSong,
    playlistId,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    handlePlayPause,
    handleSeek,
    handleVolumeChange,
    toggleMute,
    playNextSong,
    playPreviousSong
  } = useAudio();

  // Add an effect to stop loading when a song starts playing
  useEffect(() => {
    if (currentSong && isLoading) {
      setIsLoading(false);
    }
  }, [currentSong, isLoading]);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleCoverClick = () => {
    if (playlistId) {
      navigate(`/playlist/${playlistId}`);
    }
  };

  const handlePlayButtonClick = () => {
    if (currentSong) {
      // If there's a current song, just toggle play/pause
      handlePlayPause();
      console.log('currentSong', currentSong, 'duration: ', duration);
    } else {
      // If no song is playing, try to play the first available playlist
      setIsLoading(true);
      
      // Dispatch the custom event to play the first available playlist
      window.dispatchEvent(new CustomEvent('playFirstAvailablePlaylist'));
      
      // Set a timeout to reset the loading state if no song starts playing
      setTimeout(() => {
        setIsLoading(false);
      }, 3000);
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: 'background.paper',
        p: isExtraSmallScreen ? 0.5 : isSmallScreen ? 1 : 2,
        borderTop: 1,
        borderColor: 'divider',
        zIndex: 1000,
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: isExtraSmallScreen ? 0.5 : isSmallScreen ? 1 : 2,
        px: isExtraSmallScreen ? 0.5 : 1
      }}>
        {currentSong && (
          <Box 
            onClick={handleCoverClick}
            sx={{ 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              mr: isExtraSmallScreen ? 0.5 : 1,
              '&:hover': {
                opacity: 0.8
              }
            }}
          >
            <Avatar
              src={currentSong.imageUrl}
              variant="rounded"
              sx={{ 
                width: isExtraSmallScreen ? 32 : isSmallScreen ? 40 : 50, 
                height: isExtraSmallScreen ? 32 : isSmallScreen ? 40 : 50,
                bgcolor: 'primary.main'
              }}
            >
              <MusicNote />
            </Avatar>
          </Box>
        )}
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isExtraSmallScreen ? 0.5 : 1,
          minWidth: isExtraSmallScreen ? 'auto' : 'auto',
          width: 'auto'
        }}>
          {!isSmallScreen && (
            <IconButton 
              onClick={playPreviousSong} 
              size={isExtraSmallScreen ? "small" : "medium"}
              sx={{ p: isExtraSmallScreen ? 0.5 : 1 }}
            >
              <SkipPrevious />
            </IconButton>
          )}
          <IconButton 
            onClick={handlePlayButtonClick} 
            size={isExtraSmallScreen ? "small" : "medium"}
            sx={{ p: isExtraSmallScreen ? 0.5 : 1 }}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : isPlaying ? (
              <Pause />
            ) : (
              <PlayArrow />
            )}
          </IconButton>
          {!isSmallScreen && (
            <IconButton 
              onClick={playNextSong} 
              size={isExtraSmallScreen ? "small" : "medium"}
              sx={{ p: isExtraSmallScreen ? 0.5 : 1 }}
            >
              <SkipNext />
            </IconButton>
          )}
        </Box>

        <Box sx={{ 
          flex: 1, 
          mx: isExtraSmallScreen ? 0.5 : isSmallScreen ? 1 : 2,
          minWidth: 0,
          width: '100%'
        }}>
          {duration != null && duration > 0 && duration < Infinity ? (
            <>
              <Slider
                value={currentTime}
                max={duration}
                onChange={(_, newValue) => handleSeek(newValue)}
                sx={{ 
                  color: 'primary.main',
                  width: '100%',
                  '& .MuiSlider-thumb': {
                    width: isExtraSmallScreen ? 12 : 16,
                    height: isExtraSmallScreen ? 12 : 16
                  }
                }}
              />
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                mt: -0.5,
                width: '100%'
              }}>
                <Typography variant="caption" sx={{ fontSize: isExtraSmallScreen ? '0.7rem' : '0.75rem' }}>
                  {formatTime(currentTime)}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: isExtraSmallScreen ? '0.7rem' : '0.75rem' }}>
                  {formatTime(duration)}
                </Typography>
              </Box>
            </>
          ) : duration == Infinity ? (
            <>
              <Box sx={{ 
                width: '100%', 
                height: '4px', 
                bgcolor: 'primary.main',
                borderRadius: '2px',
                animation: `${pulse} 1.5s ease-in-out infinite`,
                mt: 1
              }} />
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                mt: 0.5,
                width: '100%'
              }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: isExtraSmallScreen ? '0.65rem' : '0.7rem',
                    color: 'text.secondary',
                    fontStyle: 'italic'
                  }}
                >
                  Song is generating... Seek controls will be available when complete
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ 
                width: '100%', 
                height: '4px', 
                bgcolor: 'primary.main',
                borderRadius: '2px',
                animation: `${pulse} 1.5s ease-in-out infinite`,
                mt: 1
              }} />
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                mt: 0.5,
                width: '100%'
              }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: isExtraSmallScreen ? '0.65rem' : '0.7rem',
                    color: 'text.secondary',
                    fontStyle: 'italic'
                  }}
                >
                  Press the play button to start listening
                </Typography>
              </Box>
            </>
          )
        }
        </Box>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isExtraSmallScreen ? 0.5 : 1,
          ml: 'auto'
        }}>
          <IconButton 
            onClick={toggleMute} 
            size={isExtraSmallScreen ? "small" : "medium"}
            sx={{ p: isExtraSmallScreen ? 0.5 : 1 }}
          >
            {isMuted ? <VolumeOff /> : <VolumeUp />}
          </IconButton>
          {!isExtraSmallScreen && (
            <Slider
              value={volume}
              min={0}
              max={1}
              step={0.01}
              onChange={(_, newValue) => handleVolumeChange(newValue)}
              sx={{ 
                width: isSmallScreen ? 80 : 100, 
                color: 'primary.main',
                '& .MuiSlider-thumb': {
                  width: isExtraSmallScreen ? 12 : 16,
                  height: isExtraSmallScreen ? 12 : 16
                }
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Player; 